/**
 * Reviews section - slider + read more popup
 */
if (!customElements.get('reviews-slider')) {
  customElements.define(
    'reviews-slider',
    class ReviewsSlider extends HTMLElement {
      constructor() {
        super();
        this.slider = null;
      }

      connectedCallback() {
        this.sliderWrapper = this.querySelector('.reviews__wrapper');
        this.swiperEl = this.querySelector('.reviews__inner.swiper');
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${CSS.escape(this.sectionId)}`) || this.closest('[data-section-id]');
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.showReadMore = this.dataset.showReadMore === 'true';

        if (!this.sliderWrapper) return;

        if (this.enableSlider && this.swiperEl) {
          this.initSlider();
        }

        if (this.showReadMore) {
          this.initReadMore();
        }
      }

      initSlider() {
        if (this.slider?.slider) return;

        const prevBtn = this.section?.querySelector('.swiper-button-prev');
        const nextBtn = this.section?.querySelector('.swiper-button-next');
        const paginationEl = this.querySelector('[data-pagination-container]');
        const columnsDesktop = parseInt(this.dataset.columnsDesktop || '5', 10);
        const columnsTablet = parseInt(this.dataset.columnsTablet || '3', 10);
        const columnsMobile = parseInt(this.dataset.columnsMobile || '1', 10);

        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap').trim();
        let spaceBetween = 24;
        if (columnGap) {
          const parsed = parseFloat(columnGap.replace(/rem|px/g, ''));
          if (!isNaN(parsed)) {
            spaceBetween = columnGap.includes('rem') ? parsed * 10 : parsed;
          }
        }

        this.sliderWrapper.classList.remove('f-grid');
        this.sliderWrapper.classList.add('swiper-wrapper');

        if (typeof window.FoxTheme?.Carousel !== 'function') {
          console.warn('Reviews: FoxTheme.Carousel not loaded');
          return;
        }

        const mobileSlides = columnsMobile + 0.2;
        const tabletSlides = columnsTablet;
        const desktopSlides = columnsDesktop + 0.5;

        // Configure pagination for mobile only
        const paginationConfig = paginationEl ? {
          el: paginationEl,
          type: 'bullets',
          clickable: true,
          dynamicBullets: false,
        } : false;

        this.slider = new window.FoxTheme.Carousel(this.swiperEl, {
          slidesPerView: mobileSlides,
          spaceBetween: spaceBetween,
          loop: true,
          centeredSlides: false,
          navigation: prevBtn && nextBtn ? {
            prevEl: prevBtn,
            nextEl: nextBtn,
          } : false,
          pagination: paginationConfig,
          breakpoints: {
            768: { 
              slidesPerView: tabletSlides,
              pagination: false, // Disable pagination on tablet and above
            },
            1024: { slidesPerView: desktopSlides },
          },
        });
        this.slider.init();
      }

      initReadMore() {
        const previews = this.querySelectorAll('.review-card__preview');
        const readMoreBtns = this.querySelectorAll('[data-read-more]');

        readMoreBtns.forEach((btn) => {
          const card = btn.closest('.review-card');
          const preview = card?.querySelector('.review-card__preview');
          const template = card?.querySelector('.review-card__full-content');
          if (!preview || !btn || !template) return;

          const fullContent = template.innerHTML;
          if (!fullContent) return;

          const checkTruncated = () => {
            const isTruncated = preview.scrollHeight > preview.clientHeight;
            btn.classList.toggle('hidden', !isTruncated);
          };

          checkTruncated();
          window.addEventListener('resize', checkTruncated);

          btn.addEventListener('click', () => {
            this.openModal(card, fullContent);
          });
        });
      }

      openModal(card, fullContent) {
        let modal = document.getElementById('reviews-modal-' + this.sectionId);
        if (!modal) {
          modal = document.createElement('div');
          modal.id = 'reviews-modal-' + this.sectionId;
          modal.className = 'reviews-modal';
          modal.innerHTML = `
            <div class="reviews-modal__overlay" data-close></div>
            <div class="reviews-modal__content">
              <button type="button" class="reviews-modal__close" data-close aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M15 5L5 15M5 5l10 10"/>
                </svg>
              </button>
              <div class="reviews-modal__body"></div>
            </div>
          `;
          document.body.appendChild(modal);

          modal.querySelectorAll('[data-close]').forEach((el) => {
            el.addEventListener('click', () => this.closeModal(modal));
          });
        }

        const title = card?.querySelector('.review-card__title')?.textContent || '';
        const author = card?.querySelector('.review-card__author')?.textContent || '';
        const stars = card?.querySelector('.review-card__stars')?.innerHTML || '';
        const avatar = card?.querySelector('.review-card__avatar')?.outerHTML || '';

        modal.querySelector('.reviews-modal__body').innerHTML = `
          ${title ? `<h3 class="review-card__title h4 m-0 mb-3">${title}</h3>` : ''}
          <div class="reviews-modal__full-content rte text-base text-subtext">${fullContent}</div>
          ${avatar || author || stars ? `
            <div class="review-card__footer mt-4 pt-4 flex items-center gap-3">
              ${avatar}
              <div class="min-w-0">
                ${stars ? `<div class="review-card__stars flex gap-0.5">${stars}</div>` : ''}
                ${author ? `<p class="review-card__author m-0 text-sm font-medium">${author}</p>` : ''}
              </div>
            </div>
          ` : ''}
        `;

        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }

      closeModal(modal) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }

      disconnectedCallback() {
        this.slider?.slider?.destroy?.();
        const modal = document.getElementById('reviews-modal-' + this.sectionId);
        if (modal) modal.remove();
      }
    }
  );
}

/**
 * Shopify Theme Editor integration
 * Ensure the reviews slider initializes correctly when sections
 * are reloaded/changed inside the customizer (without needing a full save).
 */
if (typeof document !== 'undefined') {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!section) return;

    const reviewsSliderEl = section.querySelector('reviews-slider');
    if (reviewsSliderEl && typeof reviewsSliderEl.initSlider === 'function') {
      // Re‑initialize the slider if enabled
      if (reviewsSliderEl.dataset.enableSlider === 'true') {
        reviewsSliderEl.initSlider();
      }
    }
  });
}
